/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { Button, Table } from 'react-bootstrap-v5'
import { useParams, Link, useHistory } from 'react-router-dom'
import { KTSVG } from '../../_metronic/helpers'
import Swal from 'sweetalert2'
import GetPagePermission from '../components/GetPagePermission'
import { formatDecimalQuantity } from '../helpers/GlobalHelper'
import clsx from 'clsx'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'

interface RouteParams {
	id: string;
}

interface QcTestRow {
	idQcTests: number;
	idBlendingOrderQcResults?: number | null;
	testName: string;
	method: string;
	limitFrom: string;
	limitTo: string;
	result: string;
	note: string;
}

interface MaterialRow {
	idBlendingOrderRows?: number;
	tempId?: string;
	isNew?: boolean;
	isQcAdded?: boolean;
	idItems?: number;
	itemName?: string;
	categoryName?: string;
	percentage?: number | null;
	quantity?: number;
	unitName?: string;
	idWarehouses?: number | '';
	warehouseName?: string;
	actualQuantity: string | number;
}

interface SelectOption {
	label: string;
	value: number;
}

/** Sum of material actual quantities (kg consumed). */
const sumMaterialActualQty = (rows: MaterialRow[]): number | null => {
	if (rows.length === 0) {
		return null
	}

	let sum = 0
	for (const row of rows) {
		if (row.actualQuantity === '' || row.actualQuantity === null || row.actualQuantity === undefined) {
			return null
		}
		const actual = parseFloat(String(row.actualQuantity))
		if (isNaN(actual)) {
			return null
		}
		sum += actual
	}

	return parseFloat(sum.toFixed(5))
}

/** Actual production (Ltr) from planned production scaled by actual vs planned batch weight. */
const calcActualProductionQuantity = (order: any, actualWeightKg: number | null): number | null => {
	if (actualWeightKg === null) {
		return null
	}

	const plannedProductionQty = parseFloat(String(order?.productionQuantity ?? 0))
	const plannedWeightKg = parseFloat(String(order?.weightInKg ?? 0))

	if (plannedWeightKg <= 0 || plannedProductionQty <= 0) {
		return null
	}

	return parseFloat(((plannedProductionQty / plannedWeightKg) * actualWeightKg).toFixed(2))
}

function WorkInProgressDetails() {
	const { id }: RouteParams = useParams()
	const history = useHistory()
	const canStartQc = GetPagePermission('/wip-qc')
	const canPackingOrders = GetPagePermission('/packing-orders')
	const [order, setOrder] = useState<any>(null)
	const [rows, setRows] = useState<MaterialRow[]>([])
	const [qcTests, setQcTests] = useState<QcTestRow[]>([])
	const [activeTab, setActiveTab] = useState<'materials' | 'qc'>('materials')
	const [saving, setSaving] = useState(false)
	const [savingQc, setSavingQc] = useState(false)
	const [warehouses, setWarehouses] = useState<SelectOption[]>([])
	const [actualProductionQtyInput, setActualProductionQtyInput] = useState('')
	const actualProductionQtyManual = useRef(false)

	const loadWarehouses = (idProjects: number) => {
		fetch(process.env.REACT_APP_API_URL + 'warehouse/' + idProjects + '/list', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options: SelectOption[] = (resp.success && Array.isArray(resp.data))
					? resp.data.map((item: any) => ({
						label: item.name,
						value: item.id_warehouses,
					}))
					: []
				setWarehouses(options)
			})
			.catch(() => setWarehouses([]))
	}

	const loadOrder = () => {
		fetch(process.env.REACT_APP_API_URL + 'blending-orders/' + id, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setOrder(resp.data)
				if (resp.data?.idProjects) {
					loadWarehouses(Number(resp.data.idProjects))
				}
				setRows((resp.rows || []).map((row: any) => {
					const rawActual = row.actualQuantity === null || row.actualQuantity === undefined || row.actualQuantity === ''
						? (row.quantity ?? '')
						: row.actualQuantity

					return {
						...row,
						isQcAdded: Boolean(row.isQcAdded),
						actualQuantity: formatDecimalQuantity(rawActual),
					}
				}))
				setQcTests((resp.qcTests || []).map((row: QcTestRow) => ({
					...row,
					limitFrom: row.limitFrom ?? '',
					limitTo: row.limitTo ?? '',
					result: row.result ?? '',
					note: row.note ?? '',
				})))

				const savedActualProd = resp.data?.actualProductionQuantity
				if (savedActualProd !== null && savedActualProd !== undefined && savedActualProd !== '') {
					setActualProductionQtyInput(formatDecimalQuantity(savedActualProd, 2))
					actualProductionQtyManual.current = true
				} else {
					actualProductionQtyManual.current = false
				}
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}

	useEffect(() => {
		if (id) {
			loadOrder()
		}
	}, [id])

	const handleActualChange = (index: number, value: string) => {
		setRows((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], actualQuantity: value }
			return updated
		})
	}

	const handleActualBlur = (index: number) => {
		setRows((prev) => {
			const updated = [...prev]
			const value = updated[index].actualQuantity
			if (value === '' || value === null || value === undefined) {
				return updated
			}
			updated[index] = {
				...updated[index],
				actualQuantity: formatDecimalQuantity(value),
			}
			return updated
		})
	}

	const handleQcChange = (index: number, field: keyof QcTestRow, value: string) => {
		setQcTests((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], [field]: value }
			return updated
		})
	}

	const loadItemOptions = (inputValue: string): Promise<any[]> => {
		if (!order?.idProjects) {
			return Promise.resolve([])
		}

		const params = new URLSearchParams({
			id_projects: String(order.idProjects),
			search: inputValue.trim(),
			limit: '40',
			page: '1',
		})

		return fetch(
			process.env.REACT_APP_API_URL + 'item-stock-adjustment/items-with-stock?' + params.toString(),
			getRequestOptions()
		)
			.then((resp) => resp.json())
			.then((resp) => {
				return (resp.status && Array.isArray(resp.data) ? resp.data : []).map((item: any) => ({
					label: item.itemCode
						? `${item.itemName} (${item.itemCode})`
						: item.itemName,
					value: item.id_items || item.idItems,
					idItems: item.id_items || item.idItems,
					itemName: item.itemName,
					itemCode: item.itemCode || '',
					categoryName: item.categoryName || item.category_name || '',
					unitName: item.unitName || item.unit_name || '',
				}))
			})
			.catch(() => [])
	}

	const handleAddMaterial = () => {
		setRows((prev) => [
			...prev,
			{
				tempId: `new-${Date.now()}-${prev.length}`,
				isNew: true,
				idItems: undefined,
				itemName: '',
				categoryName: '',
				percentage: null,
				quantity: 0,
				unitName: '',
				idWarehouses: '',
				warehouseName: '',
				actualQuantity: '',
			},
		])
	}

	const handleNewRowItemChange = (index: number, option: any) => {
		setRows((prev) => {
			const updated = [...prev]
			updated[index] = {
				...updated[index],
				idItems: option ? Number(option.value) : undefined,
				itemName: option?.itemName || '',
				categoryName: option?.categoryName || '',
				unitName: option?.unitName || '',
			}
			return updated
		})
	}

	const handleNewRowWarehouseChange = (index: number, option: SelectOption | null) => {
		setRows((prev) => {
			const updated = [...prev]
			updated[index] = {
				...updated[index],
				idWarehouses: option ? Number(option.value) : '',
				warehouseName: option?.label || '',
			}
			return updated
		})
	}

	const rowKey = (row: MaterialRow) => `${row.idItems}-${row.idWarehouses}`

	const hasDuplicateItemWarehouse = () => {
		const keys = rows
			.filter((row) => row.idItems && row.idWarehouses)
			.map((row) => rowKey(row))
		return keys.length !== new Set(keys).size
	}

	const isRowValid = (row: MaterialRow) => {
		if (row.actualQuantity === '' || row.actualQuantity === null || row.actualQuantity === undefined) {
			return false
		}
		if (isNaN(Number(row.actualQuantity)) || Number(row.actualQuantity) < 0) {
			return false
		}
		if (row.isNew) {
			return Boolean(row.idItems && row.idWarehouses)
		}
		return true
	}

	const variance = (row: MaterialRow) => {
		if (row.isNew || row.isQcAdded) {
			return ''
		}
		const actual = parseFloat(String(row.actualQuantity))
		const bom = parseFloat(String(row.quantity))
		if (isNaN(actual) || isNaN(bom)) {
			return ''
		}
		return formatDecimalQuantity(actual - bom)
	}

	const variancePercent = (row: MaterialRow) => {
		if (row.isNew || row.isQcAdded) {
			return ''
		}
		const actual = parseFloat(String(row.actualQuantity))
		const bom = parseFloat(String(row.quantity))
		if (isNaN(actual) || isNaN(bom) || bom === 0) {
			return ''
		}
		return formatDecimalQuantity(((actual - bom) / bom) * 100, 2)
	}

	const qcLocked = order?.status === 'QC Passed'
	const isOpening = order?.type === 'opening'
	const canEditActuals = !isOpening && !qcLocked && (order?.status === 'WIP' || order?.status === 'QC')
	const canAddMaterials = !isOpening && canStartQc === 1 && order?.status === 'QC' && !qcLocked

	const actualWeightInKg = useMemo(() => sumMaterialActualQty(rows), [rows])

	useEffect(() => {
		if (!order || actualProductionQtyManual.current) {
			return
		}
		const calculated = calcActualProductionQuantity(order, actualWeightInKg)
		if (calculated !== null) {
			setActualProductionQtyInput(formatDecimalQuantity(calculated, 2))
		} else if (order.productionQuantity !== null && order.productionQuantity !== undefined) {
			setActualProductionQtyInput(formatDecimalQuantity(order.productionQuantity, 2))
		}
	}, [order, actualWeightInKg, rows])

	const handleActualProductionQtyChange = (value: string) => {
		actualProductionQtyManual.current = true
		setActualProductionQtyInput(value)
	}

	const handleActualProductionQtyBlur = () => {
		if (actualProductionQtyInput === '') {
			return
		}
		setActualProductionQtyInput(formatDecimalQuantity(actualProductionQtyInput, 2))
	}

	const isActualProductionQtyValid = actualProductionQtyInput !== ''
		&& !isNaN(Number(actualProductionQtyInput))
		&& Number(actualProductionQtyInput) >= 0

	const canSave = canEditActuals
		&& rows.length > 0
		&& rows.every(isRowValid)
		&& isActualProductionQtyValid
		&& !hasDuplicateItemWarehouse()
		&& !saving

	const buildRowPayload = (row: MaterialRow) => {
		if (row.idBlendingOrderRows) {
			return {
				idBlendingOrderRows: row.idBlendingOrderRows,
				actualQuantity: Number(row.actualQuantity),
			}
		}
		return {
			idItems: row.idItems,
			idWarehouses: Number(row.idWarehouses),
			actualQuantity: Number(row.actualQuantity),
		}
	}

	const submitActuals = async (url: string, confirmText: string, confirmButtonText: string) => {
		if (!canSave) {
			return
		}

		if (hasDuplicateItemWarehouse()) {
			Swal.fire({
				icon: 'warning',
				title: 'Duplicate material',
				text: 'The same item and warehouse cannot be added more than once.',
			})
			return
		}

		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: confirmText,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText,
		})
		if (!confirm.isConfirmed) {
			return
		}

		setSaving(true)
		try {
			const response = await fetch(url, postRequestOptions({
				rows: rows.map(buildRowPayload),
				actualProductionQuantity: Number(actualProductionQtyInput),
			}))
			const data = await response.json()
			if (data.success) {
				Swal.fire({
					icon: 'success',
					title: 'Success',
					text: data.successMessage,
				}).then(() => loadOrder())
			} else {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: data.errorMessage || data.errors || 'Action failed.',
				})
			}
		} catch (error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'An error occurred.',
			})
		} finally {
			setSaving(false)
		}
	}

	const handleSave = async (event: React.FormEvent) => {
		event.preventDefault()
		await submitActuals(
			`${process.env.REACT_APP_API_URL}blending-orders/save-actual/${id}`,
			'Stock-out will be updated to the actual quantities.',
			'Yes, Save'
		)
	}

	const handleSaveQc = async (event: React.FormEvent) => {
		event.preventDefault()

		setSavingQc(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}blending-orders/save-qc/${id}`,
				postRequestOptions({
					qcTests: qcTests.map((row) => ({
						idQcTests: row.idQcTests,
						limitFrom: row.limitFrom,
						limitTo: row.limitTo,
						result: row.result,
						note: row.note,
					})),
				})
			)
			const data = await response.json()
			if (data.success) {
				Swal.fire({
					icon: 'success',
					title: 'Success',
					text: data.successMessage,
				}).then(() => loadOrder())
			} else {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: data.errorMessage || data.errors || 'Action failed.',
				})
			}
		} catch (error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'An error occurred.',
			})
		} finally {
			setSavingQc(false)
		}
	}

	if (!order) {
		return (
			<div className="card card-custom">
				<div className="card-body">Loading...</div>
			</div>
		)
	}

	return (
		<div className="card card-custom">
			<div className="card-header">
				<div className="card-title">
					<KTSVG path="/media/svg/icons/Text/Bullet-list.svg" className="svg-icon-1 svg-icon-primary" />
					<span className="text fw-bolder" style={{ marginLeft: '10px' }}>
						Work In Progress Details
					</span>
				</div>
				<div className="card-toolbar">
					{order.status === 'WIP' && !isOpening && canStartQc === 1 &&
						<button
							type="button"
							className="btn btn-sm btn-warning me-2"
							onClick={async () => {
								const confirm = await Swal.fire({
									title: 'Are you sure?',
									text: 'You want to start QC for this blending order?',
									icon: 'warning',
									showCancelButton: true,
									confirmButtonColor: '#ec008c',
									cancelButtonText: 'Cancel',
									confirmButtonText: 'Yes, Start QC',
								})
								if (!confirm.isConfirmed) {
									return
								}
								const response = await fetch(
									`${process.env.REACT_APP_API_URL}blending-orders/start-qc/${id}`,
									postRequestOptions({})
								)
								const data = await response.json()
								if (data.success) {
									Swal.fire({ icon: 'success', title: 'Success', text: data.successMessage }).then(() => loadOrder())
								} else {
									Swal.fire({ icon: 'error', title: 'Error', text: data.errorMessage || data.errors || 'Action failed.' })
								}
							}}
						>
							Start QC
						</button>
					}
					{order.status === 'QC Passed' && canPackingOrders === 1 && Number(order.remainingQty) > 0 &&
						<Link to={`/packing-order-create/${id}`} className="btn btn-sm btn-primary me-2">
							Create Packing Order
						</Link>
					}
					<Link to={isOpening ? '/wip-opening' : '/work-in-progress'} className="btn btn-sm btn-secondary">Back</Link>
				</div>
			</div>
			<div className="card-body">
				<Table size="sm" bordered>
					<tbody>
						<tr>
							<td width="30%">Production Order No</td>
							<td> : {order.productionOrderNo}</td>
						</tr>
						<tr>
							<td>Status</td>
							<td> : {order.status}{isOpening ? ' (Opening)' : ''}</td>
						</tr>
						<tr>
							<td>Project</td>
							<td> : {order.projectName}</td>
						</tr>
						<tr>
							<td>Warehouse</td>
							<td> : {order.warehouseName || '-'}</td>
						</tr>
						<tr>
							<td>Production Date</td>
							<td> : {order.productionDate}</td>
						</tr>
						<tr>
							<td>Product Name</td>
							<td> : {order.itemName}</td>
						</tr>
						<tr>
							<td>Product Code</td>
							<td> : {order.itemCode}</td>
						</tr>
						<tr>
							<td>BOM</td>
							<td> : {isOpening ? '-' : order.bomName}</td>
						</tr>
						<tr>
							<td>Production Quantity (Ltr)</td>
							<td>
								{' : Qty: '}{formatDecimalQuantity(order.productionQuantity, 2)} {order.unitName}
								{' | Actual Qty: '}
								{canEditActuals
									? (
										<>
											<input
												type="text"
												inputMode="decimal"
												className="form-control form-control-sm d-inline-block"
												style={{ width: '120px', verticalAlign: 'middle' }}
												value={actualProductionQtyInput}
												onChange={(event) => handleActualProductionQtyChange(event.target.value)}
												onBlur={handleActualProductionQtyBlur}
											/>
											{' '}{order.unitName}
										</>
									)
									: (
										<>
											{formatDecimalQuantity(
												actualProductionQtyInput || order.actualProductionQuantity || order.productionQuantity,
												2
											)}{' '}{order.unitName}
										</>
									)}
							</td>
						</tr>
						<tr>
							<td>Already Used</td>
							<td> : {formatDecimalQuantity(order.alreadyUsed)}</td>
						</tr>
						<tr>
							<td>Remaining Quantity</td>
							<td> : {formatDecimalQuantity(order.remainingQty)}</td>
						</tr>
						<tr>
							<td>Weight in KG</td>
							<td>
								{' : Qty: '}{formatDecimalQuantity(order.weightInKg)}
								{' | Actual Qty: '}
								{actualWeightInKg === null
									? formatDecimalQuantity(order.weightInKg)
									: formatDecimalQuantity(actualWeightInKg)}
							</td>
						</tr>
						<tr>
							<td>Batch No</td>
							<td> : {order.batchNo}</td>
						</tr>
						<tr>
							<td>Created By</td>
							<td> : {order.createdBy}</td>
						</tr>
					</tbody>
				</Table>

				{!isOpening &&
				<>
				<ul className="nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5">
					<li className="nav-item">
						<button
							type="button"
							className={clsx('nav-link', { active: activeTab === 'materials' })}
							onClick={() => setActiveTab('materials')}
						>
							Required Materials
						</button>
					</li>
					<li className="nav-item">
						<button
							type="button"
							className={clsx('nav-link', { active: activeTab === 'qc' })}
							onClick={() => setActiveTab('qc')}
						>
							QC Test
						</button>
					</li>
				</ul>

				{activeTab === 'materials' &&
					<form onSubmit={handleSave}>
						{canAddMaterials &&
							<div className="mb-3">
								<button
									type="button"
									className="btn btn-sm btn-light-primary"
									onClick={handleAddMaterial}
								>
									Add Material
								</button>
							</div>
						}
						<Table size="sm" bordered hover>
							<thead>
								<tr>
									<th>SN</th>
									<th>Item Name</th>
									<th>Category</th>
									<th>Percentage</th>
									<th>Quantity</th>
									<th>Unit</th>
									<th>Warehouse</th>
									<th>Actual Qty</th>
									<th>Variance</th>
									<th>Variance %</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row, index) => (
									<tr key={row.idBlendingOrderRows || row.tempId || index}>
										<td>{index + 1}</td>
										<td style={{ minWidth: '180px' }}>
											{row.isNew
												? (
													<AsyncSelect
														cacheOptions
														defaultOptions
														loadOptions={loadItemOptions}
														value={row.idItems
															? {
																label: row.itemName || '',
																value: row.idItems,
															}
															: null}
														onChange={(option) => handleNewRowItemChange(index, option)}
														placeholder="Search item..."
														className="basic-single"
														classNamePrefix="select"
													/>
												)
												: row.itemName}
										</td>
										<td>{row.categoryName || '-'}</td>
										<td>{row.isNew || row.isQcAdded ? '-' : formatDecimalQuantity(row.percentage)}</td>
										<td>{row.isNew || row.isQcAdded ? '-' : formatDecimalQuantity(row.quantity)}</td>
										<td>{row.unitName || '-'}</td>
										<td style={{ minWidth: '140px' }}>
											{row.isNew
												? (
													<Select
														value={warehouses.find((option) => Number(option.value) === Number(row.idWarehouses)) || null}
														onChange={(option) => handleNewRowWarehouseChange(index, option)}
														options={warehouses}
														placeholder="Warehouse"
														className="basic-single"
														classNamePrefix="select"
													/>
												)
												: row.warehouseName}
										</td>
										<td style={{ minWidth: '120px' }}>
											{qcLocked
												? formatDecimalQuantity(row.actualQuantity)
												: (
													<input
														type="text"
														inputMode="decimal"
														className="form-control form-control-sm"
														value={row.actualQuantity}
														onChange={(event) => handleActualChange(index, event.target.value)}
														onBlur={() => handleActualBlur(index)}
													/>
												)
											}
										</td>
										<td>{variance(row)}</td>
										<td>{variancePercent(row) === '' ? '' : variancePercent(row) + '%'}</td>
									</tr>
								))}
							</tbody>
						</Table>

						{canEditActuals &&
							<div className="form-group row mt-4">
								<div className="col text-center">
									<button type="submit" className="btn btn-sm btn-primary me-2" disabled={!canSave}>
										Save
									</button>
									{canStartQc === 1 &&
										<button
											type="button"
											className="btn btn-sm btn-success me-2"
											disabled={!canSave}
											onClick={() => submitActuals(
												`${process.env.REACT_APP_API_URL}blending-orders/pass-qc/${id}`,
												'Actual quantities will be saved and QC will be passed. No further QC can be done.',
												'Yes, Pass QC'
											)}
										>
											Pass QC
										</button>
									}
									<Button variant="secondary" size="sm" onClick={() => history.push('/work-in-progress')}>
										Close
									</Button>
								</div>
							</div>
						}
					</form>
				}

				{activeTab === 'qc' &&
					<form onSubmit={handleSaveQc}>
						<Table size="sm" bordered hover>
							<thead>
								<tr>
									<th>SN</th>
									<th>Test</th>
									<th>Method</th>
									<th>Limit From</th>
									<th>Limit To</th>
									<th>Result</th>
									<th>Note</th>
								</tr>
							</thead>
							<tbody>
								{qcTests.length === 0 && (
									<tr>
										<td colSpan={7} className="text-center">No QC tests configured for this project.</td>
									</tr>
								)}
								{qcTests.map((row, index) => (
									<tr key={row.idQcTests}>
										<td>{index + 1}</td>
										<td>{row.testName}</td>
										<td>{row.method}</td>
										<td style={{ minWidth: '120px' }}>
											<input
												type="text"
												className="form-control form-control-sm"
												value={row.limitFrom}
												onChange={(event) => handleQcChange(index, 'limitFrom', event.target.value)}
											/>
										</td>
										<td style={{ minWidth: '120px' }}>
											<input
												type="text"
												className="form-control form-control-sm"
												value={row.limitTo}
												onChange={(event) => handleQcChange(index, 'limitTo', event.target.value)}
											/>
										</td>
										<td style={{ minWidth: '120px' }}>
											<input
												type="text"
												className="form-control form-control-sm"
												value={row.result}
												onChange={(event) => handleQcChange(index, 'result', event.target.value)}
											/>
										</td>
										<td style={{ minWidth: '160px' }}>
											<input
												type="text"
												className="form-control form-control-sm"
												value={row.note}
												onChange={(event) => handleQcChange(index, 'note', event.target.value)}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</Table>

						<div className="form-group row mt-4">
							<div className="col text-center">
								<button type="submit" className="btn btn-sm btn-primary me-2" disabled={savingQc || qcTests.length === 0}>
									Save QC
								</button>
								<Button variant="secondary" size="sm" onClick={() => history.push('/work-in-progress')}>
									Close
								</Button>
							</div>
						</div>
					</form>
				}
				</>
				}
			</div>
		</div>
	)
}

export default WorkInProgressDetails
